/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useId, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface CheckoutAssortmentNameFieldProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export function CheckoutAssortmentNameField({
    value,
    onChange,
    disabled = false,
}: CheckoutAssortmentNameFieldProps): ReactElement {
    const { t } = useTranslation('checkout');
    const fieldId = useId();

    return (
        <div className="flex flex-col gap-2" data-testid="checkout-assortment-name-field">
            <Label htmlFor={fieldId} className="text-sm font-medium text-foreground">
                {t('assortmentName.label', { defaultValue: 'Assortment Name' })}
            </Label>
            <Input
                id={fieldId}
                name="assortmentName"
                type="text"
                autoComplete="off"
                value={value}
                placeholder={t('assortmentName.placeholder', { defaultValue: 'Enter assortment name' })}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                onBlur={(event) => onChange(event.target.value.trim())}
            />
        </div>
    );
}
